import { get, post, put, del } from '@/api/lib/amplify-wrapper'
import {
  PerformanceParameter,
  EvaluationItem,
  Employee,
  DashboardSummary,
  CreatePerformanceParameterData,
  UpdatePerformanceParameterData,
  CreateEvaluationItemData,
  UpdateEvaluationItemData,
  PerformanceParametersListResponse,
  EvaluationItemsListResponse,
  EmployeeListResponse,
  PerformanceByTheme,
  FeedbackTrends,
  Evaluation,
  EvaluationScore,
  EvaluationTemplate,
  CreateEvaluationData,
  UpdateEvaluationData,
  CreateEvaluationScoreData,
  CreateEvaluationTemplateData,
  EvaluationsListResponse,
  EvaluationScoresListResponse,
  EvaluationTemplatesListResponse,
  ManagerDashboardSummaryResponse,
  TeamPerformanceResponse,
  IndividualProgressResponse,
  ClimateTrends,
  TeamInsights,
  Feedback,
  FeedbacksListResponse,
  OneOnOneMeeting,
  OneOnOneMeetingsListResponse,
  ComparativeAnalytics,
  OKRProgressAnalytics,
  EvaluationCategory,
  EvaluationCategoriesListResponse,
  CreateEvaluationCategoryData,
  UpdateEvaluationCategoryData,
  EvaluationConfig,
  OKR,
  CreateOKRData,
  UpdateOKRData,
  OKRsListResponse,
  KeyResult,
  CreateKeyResultData,
  UpdateKeyResultData,
  KeyResultsListResponse
} from '@/types/performance'
import { createImpersonatedApiHeaders } from '@/lib/services/api-utils'

const API_NAME = 'performance'

/**
 * Helper function to serialize data for API requests
 * Converts Date objects to ISO strings for JSON serialization
 */
const serializeData = (data: any) => {
  return JSON.parse(JSON.stringify(data, (_key, value) => {
    // Convert Date objects to ISO strings
    if (value instanceof Date) {
      return value.toISOString()
    }
    return value
  }))
}

/**
 * Helper function to extract pagination metadata from response headers
 * Follows the RESTful API Pattern for pagination headers:
 * - X-Item-Count: Number of items in the response
 * - X-Next-Token: Base64-encoded pagination token
 * - Link: RFC 5988 compliant next page link
 */
const extractPaginationMetadata = (
  headers: any,
  itemsLength: number
): {
  nextToken?: string
  itemCount: number
  link?: string
} => {
  // Extract headers (case-insensitive)
  const nextToken = headers?.['x-next-token'] || headers?.['X-Next-Token']
  const itemCount = headers?.['x-item-count'] || headers?.['X-Item-Count']
  const link = headers?.['link'] || headers?.['Link']

  return {
    nextToken: nextToken || undefined,
    itemCount: itemCount ? parseInt(itemCount, 10) : itemsLength,
    link: link || undefined
  }
}

// ============================================================================
// Performance Parameters API Functions
// ============================================================================

/**
 * Create a new performance parameter
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param data - The performance parameter data to create
 * @returns Promise resolving to the created PerformanceParameter
 */
export const createPerformanceParameter = async (
  tenantDomain: string,
  data: CreatePerformanceParameterData
): Promise<PerformanceParameter> => {
  const path = '/performance/performance-parameters'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as PerformanceParameter
}

/**
 * Get a performance parameter by ID
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param parameterId - The unique identifier of the performance parameter
 * @returns Promise resolving to the PerformanceParameter
 */
export const getPerformanceParameter = async (
  tenantDomain: string,
  parameterId: string
): Promise<PerformanceParameter> => {
  const path = `/performance/performance-parameters/${parameterId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as PerformanceParameter
}

/**
 * List all performance parameters with optional filtering and pagination
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination and filtering parameters
 * @returns Promise resolving to paginated list of PerformanceParameters
 */
export const listPerformanceParameters = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'active' | 'inactive'
  }
): Promise<PerformanceParametersListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
  if (params?.status) queryParams.append('status', params.status)

  const path = '/performance/performance-parameters' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as PerformanceParameter[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

// ============================================================================
// Dashboard API Functions
// ============================================================================

/**
 * Get current user's dashboard summary
 *
 * Returns a comprehensive dashboard summary including feedback statistics,
 * upcoming 1-on-1 meetings, performance metrics, and climate satisfaction.
 * This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to the current user's DashboardSummary
 */
export const getDashboardSummary = async (
  tenantDomain: string
): Promise<DashboardSummary> => {
  const path = '/performance/dashboard/me'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as DashboardSummary
}

/**
 * Update an existing performance parameter
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param parameterId - The unique identifier of the performance parameter to update
 * @param updates - The fields to update
 * @returns Promise resolving to the updated PerformanceParameter
 */
export const updatePerformanceParameter = async (
  tenantDomain: string,
  parameterId: string,
  updates: UpdatePerformanceParameterData
): Promise<PerformanceParameter> => {
  const path = `/performance/performance-parameters/${parameterId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(updates),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as PerformanceParameter
}

// ============================================================================
// Evaluation Categories API Functions
// ============================================================================

/**
 * Create a new evaluation category
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param data - The evaluation category data to create
 * @returns Promise resolving to the created EvaluationCategory
 */
export const createEvaluationCategory = async (
  tenantDomain: string,
  data: CreateEvaluationCategoryData
): Promise<EvaluationCategory> => {
  const path = '/performance/evaluation-categories'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as EvaluationCategory
}

/**
 * Get an evaluation category by ID
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param categoryId - The unique identifier of the evaluation category
 * @returns Promise resolving to the EvaluationCategory
 */
export const getEvaluationCategory = async (
  tenantDomain: string,
  categoryId: string
): Promise<EvaluationCategory> => {
  const path = `/performance/evaluation-categories/${categoryId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as EvaluationCategory
}

/**
 * List all evaluation categories with optional filtering
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional filtering parameters
 * @returns Promise resolving to list of EvaluationCategories
 */
export const listEvaluationCategories = async (
  tenantDomain: string,
  params?: {
    status?: 'active' | 'inactive'
  }
): Promise<EvaluationCategoriesListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.status) queryParams.append('status', params.status)

  const path = '/performance/evaluation-categories' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as EvaluationCategory[]

  return { items }
}

/**
 * Update an evaluation category
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param categoryId - The unique identifier of the evaluation category
 * @param data - The update data
 * @returns Promise resolving to the updated EvaluationCategory
 */
export const updateEvaluationCategory = async (
  tenantDomain: string,
  categoryId: string,
  data: UpdateEvaluationCategoryData
): Promise<EvaluationCategory> => {
  const path = `/performance/evaluation-categories/${categoryId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as EvaluationCategory
}

/**
 * Delete an evaluation category (soft delete)
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param categoryId - The unique identifier of the evaluation category
 * @returns Promise resolving when deletion is complete
 */
export const deleteEvaluationCategory = async (
  tenantDomain: string,
  categoryId: string
): Promise<void> => {
  const path = `/performance/evaluation-categories/${categoryId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

// ============================================================================
// Evaluation Items API Functions
// ============================================================================

/**
 * Create a new evaluation item
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param data - The evaluation item data to create
 * @returns Promise resolving to the created EvaluationItem
 */
export const createEvaluationItem = async (
  tenantDomain: string,
  data: CreateEvaluationItemData
): Promise<EvaluationItem> => {
  const path = '/performance/evaluation-items'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as EvaluationItem
}

/**
 * Get an evaluation item by ID
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param itemId - The unique identifier of the evaluation item
 * @returns Promise resolving to the EvaluationItem
 */
export const getEvaluationItem = async (
  tenantDomain: string,
  itemId: string
): Promise<EvaluationItem> => {
  const path = `/performance/evaluation-items/${itemId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as EvaluationItem
}

/**
 * List all evaluation items with optional filtering and pagination
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination and filtering parameters
 * @returns Promise resolving to paginated list of EvaluationItems
 */
export const listEvaluationItems = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'active' | 'inactive'
  }
): Promise<EvaluationItemsListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
  if (params?.status) queryParams.append('status', params.status)

  const path = '/performance/evaluation-items' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as EvaluationItem[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

/**
 * List evaluation items filtered by role ID with pagination
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param roleId - The role ID to filter evaluation items by
 * @param params - Optional pagination parameters
 * @returns Promise resolving to paginated list of EvaluationItems for the specified role
 */
export const listEvaluationItemsByRole = async (
  tenantDomain: string,
  roleId: string,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<EvaluationItemsListResponse> => {
  const queryParams = new URLSearchParams()

  queryParams.append('roleId', roleId)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = `/performance/evaluation-items?${queryParams.toString()}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as EvaluationItem[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

/**
 * List evaluation items applicable to an employee based on their role and roleGroup
 *
 * This function fetches evaluation items that match either the employee's roleId
 * or their roleGroup, supporting the hybrid role/roleGroup assignment model.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param roleId - The employee's role ID
 * @param roleGroup - The employee's role group name
 * @returns Promise resolving to paginated list of EvaluationItems
 */
export const listEvaluationItemsForEmployee = async (
  tenantDomain: string,
  roleId: string,
  roleGroup: string
): Promise<EvaluationItemsListResponse> => {
  const queryParams = new URLSearchParams()

  if (roleId) queryParams.append('roleId', roleId)
  if (roleGroup) queryParams.append('roleGroup', roleGroup)

  const path = `/performance/evaluation-items/for-employee?${queryParams.toString()}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as EvaluationItem[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

/**
 * Update an existing evaluation item
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param itemId - The unique identifier of the evaluation item to update
 * @param updates - The fields to update
 * @returns Promise resolving to the updated EvaluationItem
 */
export const updateEvaluationItem = async (
  tenantDomain: string,
  itemId: string,
  updates: UpdateEvaluationItemData
): Promise<EvaluationItem> => {
  const path = `/performance/evaluation-items/${itemId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(updates),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as EvaluationItem
}

/**
 * Delete an evaluation item (soft delete - sets status to inactive)
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param itemId - The unique identifier of the evaluation item to delete
 * @returns Promise resolving to the deleted EvaluationItem (with status='inactive')
 */
export const deleteEvaluationItem = async (
  tenantDomain: string,
  itemId: string
): Promise<EvaluationItem> => {
  const path = `/performance/evaluation-items/${itemId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as EvaluationItem
}

// ============================================================================
// Evaluation Categories API Functions
// ============================================================================

/**
 * Get all evaluation categories with their display configuration
 *
 * Returns the dynamic category configuration that replaces the hardcoded
 * EVALUATION_THEMES array. Includes icon, colors, descriptions, and order.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to list of EvaluationCategories
 */
export const getEvaluationCategories = async (
  tenantDomain: string
): Promise<EvaluationCategoriesListResponse> => {
  const path = '/performance/evaluation-categories'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as EvaluationCategory[]

  return {
    items
  }
}

/**
 * Get evaluation configuration (rules, thresholds, score labels)
 *
 * Returns the global evaluation configuration including thresholds,
 * validation rules, and score labels. Replaces hardcoded configuration values.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to EvaluationConfig
 */
export const getEvaluationConfig = async (
  tenantDomain: string
): Promise<EvaluationConfig> => {
  const path = '/performance/evaluation-config'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as EvaluationConfig
}

// ============================================================================
// Employee API Functions
// ============================================================================

/**
 * Get current user's employee profile
 *
 * Returns the authenticated user's employee profile including role, department,
 * and manager information. This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to the current user's Employee profile
 */
export const getMyEmployeeProfile = async (
  tenantDomain: string
): Promise<Employee> => {
  const path = '/performance/employees/me'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as Employee
}

/**
 * List employees with pagination (managers only)
 *
 * Returns a paginated list of active employees. Requires manager, hr-admin, or
 * super-admin role. Useful for selecting employees for evaluations, feedback,
 * and other performance management activities.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination parameters
 * @returns Promise resolving to paginated list of Employees
 */
export const listEmployees = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<EmployeeListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = '/performance/employees' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as Employee[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

/**
 * List all employees for HR users (with search capability)
 *
 * Returns a searchable list of all active employees. Requires HR role:
 * 'rh', 'analista-rh', 'admin', 'super-admin', 'hr-admin'.
 * Used for HR evaluations where any employee can be selected.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional search and pagination parameters
 * @returns Promise resolving to list of Employees with search filtering
 */
export const listAllEmployeesForHR = async (
  tenantDomain: string,
  params?: {
    search?: string
    limit?: number
    nextToken?: string
  }
): Promise<EmployeeListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.search) queryParams.append('search', params.search)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = '/performance/employees/all' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as Employee[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

/**
 * List peer employees at same hierarchy level
 *
 * Returns list of peer employees who share the same manager or department.
 * Used for peer evaluations where evaluators select colleagues at similar level.
 * Any authenticated user can access their peers list.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional limit parameter
 * @returns Promise resolving to list of peer Employees
 */
export const listPeerEmployees = async (
  tenantDomain: string,
  params?: {
    limit?: number
  }
): Promise<EmployeeListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())

  const path = '/performance/employees/peers' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as Employee[]

  return {
    items,
    pagination: { itemCount: items.length }
  }
}

// ============================================================================
// Analytics API Functions
// ============================================================================

/**
 * Get current user's performance breakdown by theme
 *
 * Returns performance analytics grouped by evaluation theme/category with average scores.
 * This endpoint follows the /me self-service pattern where any authenticated user can
 * access their own performance analytics.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to array of PerformanceByTheme objects
 */
export const getPerformanceByTheme = async (
  tenantDomain: string
): Promise<PerformanceByTheme[]> => {
  const path = '/performance/analytics/performance-by-theme/me'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as PerformanceByTheme[]
}

/**
 * Get current user's feedback trends over time
 *
 * Returns time-series data of feedback by type and period.
 * This endpoint follows the /me self-service pattern where any authenticated user can
 * access their own feedback trends.
 *
 * Phase 1: Returns empty structure (no feedback data yet)
 * Phase 3: Will populate with real feedback data
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional filtering parameters
 * @param params.startDate - Filter start date (ISO 8601 format, e.g., "2025-01-01")
 * @param params.endDate - Filter end date (ISO 8601 format, e.g., "2025-12-31")
 * @returns Promise resolving to FeedbackTrends object
 */
export const getFeedbackTrends = async (
  tenantDomain: string,
  params?: {
    startDate?: string
    endDate?: string
  }
): Promise<FeedbackTrends> => {
  const queryParams = new URLSearchParams()

  if (params?.startDate) queryParams.append('startDate', params.startDate)
  if (params?.endDate) queryParams.append('endDate', params.endDate)

  const path = '/performance/analytics/feedback-trends/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as FeedbackTrends
}

// ============================================================================
// PHASE 2: EVALUATIONS MODULE - API FUNCTIONS
// ============================================================================

// ============================================================================
// Evaluations API Functions
// ============================================================================

/**
 * Create a new evaluation
 *
 * @param tenantDomain - Tenant domain for headers
 * @param data - Evaluation creation data
 * @returns Created evaluation
 * @throws Error if API call fails
 */
export const createEvaluation = async (
  tenantDomain: string,
  data: CreateEvaluationData
): Promise<Evaluation> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/performance/evaluations',
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as Evaluation
  } catch (error) {
    console.error('Error creating evaluation:', error)
    throw error
  }
}

/**
 * Get current user's evaluations
 *
 * @param tenantDomain - Tenant domain for headers
 * @param params - Optional query parameters (limit, nextToken, status)
 * @returns Paginated list of evaluations
 * @throws Error if API call fails
 */
export const getMyEvaluations = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'draft' | 'submitted' | 'reviewed' | 'completed' | 'all'
  }
): Promise<EvaluationsListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)

    const path = '/performance/evaluations/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as Evaluation[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error getting my evaluations:', error)
    throw error
  }
}

/**
 * Get evaluations created/performed BY the current user (as evaluator).
 * Queries GSI3 (evaluatorId). Returns evaluations the user made of OTHER people.
 */
export const getMyCreatedEvaluations = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'draft' | 'submitted' | 'reviewed' | 'completed' | 'all'
  }
): Promise<EvaluationsListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)

    const path = '/performance/evaluations/created-by-me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as Evaluation[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error getting my created evaluations:', error)
    throw error
  }
}

/**
 * Get ALL evaluations for the tenant (admin view)
 */
export const getAllEvaluations = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'draft' | 'submitted' | 'reviewed' | 'completed' | 'all'
  }
): Promise<EvaluationsListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)

    const path = '/performance/evaluations' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as Evaluation[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error getting all evaluations:', error)
    throw error
  }
}

/**
 * Get ALL individual evaluations for the tenant (admin/VP/HR view)
 */
export const getAllIndividualEvaluations = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: string
    department?: string
    search?: string
  }
) => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)
    if (params?.department) queryParams.append('department', params.department)
    if (params?.search) queryParams.append('search', params.search)

    const path = '/performance/evaluations/individual-all' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as any[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error getting all individual evaluations:', error)
    throw error
  }
}

/**
 * Delete an evaluation
 */
export const deleteEvaluation = async (
  tenantDomain: string,
  evaluationId: string
): Promise<void> => {
  try {
    const restOperation = del({
      apiName: API_NAME,
      path: `/performance/evaluations/${evaluationId}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    await restOperation.response
  } catch (error) {
    console.error('Error deleting evaluation:', error)
    throw error
  }
}

/**
 * Get evaluations for a specific employee
 *
 * @param tenantDomain - Tenant domain for headers
 * @param employeeId - Employee ID to get evaluations for
 * @param params - Optional query parameters (limit, nextToken, status)
 * @returns Paginated list of evaluations
 * @throws Error if API call fails
 */
export const getEmployeeEvaluations = async (
  tenantDomain: string,
  employeeId: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'draft' | 'submitted' | 'reviewed' | 'completed' | 'all'
  }
): Promise<EvaluationsListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)

    const path = `/performance/evaluations/employee/${employeeId}` + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as Evaluation[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error(`Error getting evaluations for employee ${employeeId}:`, error)
    throw error
  }
}

/**
 * Get a single evaluation by ID
 *
 * @param tenantDomain - Tenant domain for headers
 * @param evaluationId - Evaluation ID
 * @returns Evaluation record
 * @throws Error if API call fails
 */
export const getEvaluation = async (
  tenantDomain: string,
  evaluationId: string
): Promise<Evaluation> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/performance/evaluations/${evaluationId}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as Evaluation
  } catch (error) {
    console.error(`Error getting evaluation ${evaluationId}:`, error)
    throw error
  }
}

/**
 * Update an existing evaluation
 *
 * @param tenantDomain - Tenant domain for headers
 * @param evaluationId - Evaluation ID to update
 * @param updates - Fields to update
 * @returns Updated evaluation
 * @throws Error if API call fails
 */
export const updateEvaluation = async (
  tenantDomain: string,
  evaluationId: string,
  updates: UpdateEvaluationData
): Promise<Evaluation> => {
  try {
    const restOperation = put({
      apiName: API_NAME,
      path: `/performance/evaluations/${evaluationId}`,
      options: {
        body: serializeData(updates),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as Evaluation
  } catch (error) {
    console.error(`Error updating evaluation ${evaluationId}:`, error)
    throw error
  }
}

/**
 * Save a draft evaluation
 *
 * @param tenantDomain - Tenant domain for headers
 * @param data - Evaluation creation data
 * @returns Created draft evaluation
 * @throws Error if API call fails
 */
export const saveDraftEvaluation = async (
  tenantDomain: string,
  data: CreateEvaluationData
): Promise<Evaluation> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/performance/evaluations/draft',
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as Evaluation
  } catch (error) {
    console.error('Error saving draft evaluation:', error)
    throw error
  }
}

/**
 * Submit an evaluation
 *
 * @param tenantDomain - Tenant domain for headers
 * @param evaluationId - Evaluation ID to submit
 * @returns Updated evaluation with submitted status
 * @throws Error if API call fails
 */
export const submitEvaluation = async (
  tenantDomain: string,
  evaluationId: string
): Promise<Evaluation> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: `/performance/evaluations/${evaluationId}/submit`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as Evaluation
  } catch (error) {
    console.error(`Error submitting evaluation ${evaluationId}:`, error)
    throw error
  }
}

// ============================================================================
// Evaluation Scores API Functions
// ============================================================================

/**
 * Add evaluation scores to an evaluation
 *
 * @param tenantDomain - Tenant domain for headers
 * @param evaluationId - Evaluation ID to add scores to
 * @param scores - Score data (single object or array of scores)
 * @returns Created score(s)
 * @throws Error if API call fails
 */
export const addEvaluationScores = async (
  tenantDomain: string,
  evaluationId: string,
  scores: CreateEvaluationScoreData | CreateEvaluationScoreData[]
): Promise<EvaluationScore | EvaluationScore[]> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: `/performance/evaluations/${evaluationId}/scores`,
      options: {
        body: serializeData(scores),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as EvaluationScore | EvaluationScore[]
  } catch (error) {
    console.error(`Error adding scores to evaluation ${evaluationId}:`, error)
    throw error
  }
}

/**
 * Get evaluation scores for an evaluation
 *
 * @param tenantDomain - Tenant domain for headers
 * @param evaluationId - Evaluation ID to get scores for
 * @param params - Optional query parameters (limit, nextToken)
 * @returns Paginated list of evaluation scores
 * @throws Error if API call fails
 */
export const getEvaluationScores = async (
  tenantDomain: string,
  evaluationId: string,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<EvaluationScoresListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

    const path = `/performance/evaluations/${evaluationId}/scores` + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as EvaluationScore[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error(`Error getting scores for evaluation ${evaluationId}:`, error)
    throw error
  }
}

/**
 * Update an evaluation score
 *
 * @param tenantDomain - Tenant domain for headers
 * @param scoreId - Score ID to update
 * @param updates - Fields to update (score, comments, evidence, developmentPlan)
 * @returns Updated evaluation score
 * @throws Error if API call fails
 */
export const updateEvaluationScore = async (
  tenantDomain: string,
  scoreId: string,
  updates: Partial<CreateEvaluationScoreData>
): Promise<EvaluationScore> => {
  try {
    const restOperation = put({
      apiName: API_NAME,
      path: `/performance/evaluation-scores/${scoreId}`,
      options: {
        body: serializeData(updates),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as EvaluationScore
  } catch (error) {
    console.error(`Error updating evaluation score ${scoreId}:`, error)
    throw error
  }
}

// ============================================================================
// Evaluation Templates API Functions
// ============================================================================

/**
 * Create a new evaluation template
 *
 * @param tenantDomain - Tenant domain for headers
 * @param data - Template creation data
 * @returns Created evaluation template
 * @throws Error if API call fails
 */
export const createEvaluationTemplate = async (
  tenantDomain: string,
  data: CreateEvaluationTemplateData
): Promise<EvaluationTemplate> => {
  try {
    const restOperation = post({
      apiName: API_NAME,
      path: '/performance/evaluation-templates',
      options: {
        body: serializeData(data),
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const responseData = await response.body.json()
    return responseData as unknown as EvaluationTemplate
  } catch (error) {
    console.error('Error creating evaluation template:', error)
    throw error
  }
}

/**
 * Get current user's evaluation template
 *
 * @param tenantDomain - Tenant domain for headers
 * @returns Evaluation template for current user's role, or null if none exists
 * @throws Error if API call fails
 */
export const getMyEvaluationTemplate = async (
  tenantDomain: string
): Promise<EvaluationTemplate | null> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: '/performance/evaluation-templates/me',
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as EvaluationTemplate | null
  } catch (error) {
    console.error('Error getting my evaluation template:', error)
    throw error
  }
}

/**
 * Get evaluation templates with optional filtering
 *
 * @param tenantDomain - Tenant domain for headers
 * @param params - Optional query parameters (status, limit, nextToken)
 * @returns Paginated list of evaluation templates
 * @throws Error if API call fails
 */
export const getEvaluationTemplates = async (
  tenantDomain: string,
  params?: {
    status?: 'active' | 'inactive'
    limit?: number
    nextToken?: string
  }
): Promise<EvaluationTemplatesListResponse> => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.status) queryParams.append('status', params.status)
    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

    const path = '/performance/evaluation-templates' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as EvaluationTemplate[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error getting evaluation templates:', error)
    throw error
  }
}

/**
 * Get evaluation template by role ID
 *
 * @param tenantDomain - Tenant domain for headers
 * @param roleId - Role ID to get template for
 * @returns Evaluation template for the role, or null if none exists
 * @throws Error if API call fails
 */
export const getEvaluationTemplateByRole = async (
  tenantDomain: string,
  roleId: string
): Promise<EvaluationTemplate | null> => {
  try {
    const restOperation = get({
      apiName: API_NAME,
      path: `/performance/evaluation-templates/${roleId}`,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    return data as unknown as EvaluationTemplate | null
  } catch (error) {
    console.error(`Error getting evaluation template for role ${roleId}:`, error)
    throw error
  }
}

// ============================================================================
// PHASE 6: DASHBOARD AGGREGATIONS - API FUNCTIONS
// ============================================================================

/**
 * Get manager dashboard summary with team-wide aggregations
 *
 * Returns team-wide performance metrics for managers including feedback stats,
 * performance averages, meeting counts, and climate satisfaction.
 * Requires manager, hr-admin, or super-admin role.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to manager dashboard summary with metadata
 */
export const getManagerDashboardSummary = async (
  tenantDomain: string
): Promise<ManagerDashboardSummaryResponse> => {
  const path = '/performance/dashboard/summary'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as ManagerDashboardSummaryResponse
}

/**
 * Get team performance metrics with distribution analysis
 *
 * Returns detailed team performance analysis including individual employee
 * scores, team averages, and distribution across performance categories.
 * Requires manager, hr-admin, or super-admin role.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional query parameters
 * @param params.departmentId - Filter by department ID (optional)
 * @param params.limit - Number of employees to return (default: 25, max: 100)
 * @param params.nextToken - Pagination token (optional)
 * @returns Promise resolving to team performance metrics with pagination
 */
export const getTeamPerformance = async (
  tenantDomain: string,
  params?: {
    departmentId?: string
    limit?: number
    nextToken?: string
  }
): Promise<TeamPerformanceResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.departmentId) queryParams.append('departmentId', params.departmentId)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = '/performance/dashboard/team-performance' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as TeamPerformanceResponse
}

/**
 * Get individual progress for current user
 *
 * Returns current user's comprehensive performance tracking including active OKRs,
 * recent evaluations (last 90 days), feedback statistics, and upcoming 1-on-1 meetings.
 * This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to current user's individual progress data
 */
export const getMyIndividualProgress = async (
  tenantDomain: string
): Promise<IndividualProgressResponse> => {
  const path = '/performance/dashboard/individual-progress/me'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as IndividualProgressResponse
}

/**
 * Get individual progress for specific employee (managers only)
 *
 * Returns comprehensive performance tracking for a specific employee.
 * Requires manager, hr-admin, or super-admin role.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param employeeId - Employee ID to get progress for
 * @returns Promise resolving to employee's individual progress data
 */
export const getEmployeeProgress = async (
  tenantDomain: string,
  employeeId: string
): Promise<IndividualProgressResponse> => {
  const path = `/performance/dashboard/individual-progress/${employeeId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as IndividualProgressResponse
}

// ============================================================================
// PHASE 6.3: CLIMATE TRENDS & TEAM INSIGHTS - API FUNCTIONS
// ============================================================================

/**
 * Get climate survey trends analysis
 *
 * Returns comprehensive climate survey trend analysis across multiple periods
 * with heatmap data, category trends, and summary insights. Any authenticated
 * user can access climate trends for their organization.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional query parameters
 * @param params.departmentId - Filter by department ID (optional)
 * @param params.teamId - Filter by team ID (optional)
 * @param params.period - Number of months to analyze (default: 12, options: 3, 6, 12, 24)
 * @param params.category - Filter by specific category (optional)
 * @returns Promise resolving to climate trends analysis
 */
export const getClimateTrends = async (
  tenantDomain: string,
  params?: {
    departmentId?: string
    teamId?: string
    period?: number
    category?: string
  }
): Promise<ClimateTrends> => {
  const queryParams = new URLSearchParams()

  if (params?.departmentId) queryParams.append('departmentId', params.departmentId)
  if (params?.teamId) queryParams.append('teamId', params.teamId)
  if (params?.period) queryParams.append('period', params.period.toString())
  if (params?.category) queryParams.append('category', params.category)

  const path = '/performance/analytics/climate-trends' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as ClimateTrends
}

/**
 * Get team insights for managers
 *
 * Returns advanced team insights and patterns for managers including
 * engagement patterns, performance distribution, and actionable recommendations.
 * Requires manager, hr-admin, or super-admin role.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional query parameters
 * @param params.departmentId - Filter by department ID (optional)
 * @param params.teamId - Filter by team ID (optional)
 * @param params.period - Number of days to analyze (default: 90, options: 30, 60, 90, 180)
 * @returns Promise resolving to team insights with patterns and recommendations
 */
export const getTeamInsights = async (
  tenantDomain: string,
  params?: {
    departmentId?: string
    teamId?: string
    period?: number
  }
): Promise<TeamInsights> => {
  const queryParams = new URLSearchParams()

  if (params?.departmentId) queryParams.append('departmentId', params.departmentId)
  if (params?.teamId) queryParams.append('teamId', params.teamId)
  if (params?.period) queryParams.append('period', params.period.toString())

  const path = '/performance/analytics/team-insights' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as TeamInsights
}

// ============================================================================
// PHASE 6.2: COMPARATIVE & OKR ANALYTICS - API FUNCTIONS
// ============================================================================

/**
 * Get comparative analytics for current user
 *
 * Returns comparative analytics showing how the current user's performance
 * compares to team and company averages with anonymized benchmarks.
 * This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to comparative analytics with benchmarks
 */
export const getComparativeAnalytics = async (
  tenantDomain: string
): Promise<ComparativeAnalytics> => {
  const path = '/performance/analytics/comparative/me'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as ComparativeAnalytics
}

/**
 * Get OKR progress analytics for current user
 *
 * Returns detailed OKR progress tracking with trend analysis and completion
 * trajectory predictions. This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional query parameters
 * @param params.period - Number of days to analyze (default: 90, options: 30, 60, 90, 180)
 * @param params.granularity - Data point granularity (default: 'weekly', options: 'daily', 'weekly', 'monthly')
 * @returns Promise resolving to OKR progress analytics with trend data
 */
export const getOKRProgress = async (
  tenantDomain: string,
  params?: {
    period?: number
    granularity?: 'daily' | 'weekly' | 'monthly'
  }
): Promise<OKRProgressAnalytics> => {
  const queryParams = new URLSearchParams()

  if (params?.period) queryParams.append('period', params.period.toString())
  if (params?.granularity) queryParams.append('granularity', params.granularity)

  const path = '/performance/analytics/okr-progress/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OKRProgressAnalytics
}

// ============================================================================
// OKRs API Functions
// ============================================================================

/**
 * List OKRs with optional filtering
 * GET /performance/okrs
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional filtering and pagination parameters
 * @returns Promise resolving to paginated list of OKRs
 */
export const listOKRs = async (
  tenantDomain: string,
  params?: {
    type?: 'company' | 'team' | 'individual'
    status?: 'draft' | 'active' | 'completed' | 'cancelled'
    limit?: number
    nextToken?: string
  }
): Promise<OKRsListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.type) queryParams.append('type', params.type)
  if (params?.status) queryParams.append('status', params.status)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = `/performance/okrs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as OKR[]

  return {
    items,
    ...extractPaginationMetadata(response.headers, items.length)
  }
}

/**
 * Get current user's OKRs
 * GET /performance/okrs/me
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination parameters
 * @returns Promise resolving to paginated list of user's OKRs
 */
export const getMyOKRs = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<OKRsListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = `/performance/okrs/me${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as OKR[]

  return {
    items,
    ...extractPaginationMetadata(response.headers, items.length)
  }
}

/**
 * List OKRs by employee ID (managers only)
 * GET /performance/okrs/employee/{employeeId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param employeeId - The employee ID to list OKRs for
 * @param params - Optional filter and pagination parameters
 * @returns Promise resolving to paginated list of employee's OKRs
 */
export const listOKRsByEmployee = async (
  tenantDomain: string,
  employeeId: string,
  params?: {
    status?: string
    limit?: number
    nextToken?: string
  }
): Promise<OKRsListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.status) queryParams.append('status', params.status)
  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)

  const path = `/performance/okrs/employee/${employeeId}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as OKR[]

  return {
    items,
    ...extractPaginationMetadata(response.headers, items.length)
  }
}

/**
 * Get OKR by ID
 * GET /performance/okrs/{okrId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param okrId - The unique identifier of the OKR
 * @returns Promise resolving to the OKR
 */
export const getOKR = async (
  tenantDomain: string,
  okrId: string
): Promise<OKR> => {
  const path = `/performance/okrs/${okrId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as unknown as OKR
}

/**
 * Create a new OKR
 * POST /performance/okrs
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param data - The OKR data to create
 * @returns Promise resolving to the created OKR
 */
export const createOKR = async (
  tenantDomain: string,
  data: CreateOKRData
): Promise<OKR> => {
  const path = '/performance/okrs'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as OKR
}

/**
 * Update an existing OKR
 * PUT /performance/okrs/{okrId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param okrId - The unique identifier of the OKR to update
 * @param data - The fields to update
 * @returns Promise resolving to the updated OKR
 */
export const updateOKR = async (
  tenantDomain: string,
  okrId: string,
  data: UpdateOKRData
): Promise<OKR> => {
  const path = `/performance/okrs/${okrId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as OKR
}

/**
 * Delete an OKR (soft delete)
 * DELETE /performance/okrs/{okrId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param okrId - The unique identifier of the OKR to delete
 * @returns Promise resolving when deletion is complete
 */
export const deleteOKR = async (
  tenantDomain: string,
  okrId: string
): Promise<void> => {
  const path = `/performance/okrs/${okrId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

// ============================================================================
// Key Results API Functions
// ============================================================================

/**
 * Create a new Key Result for an OKR
 * POST /performance/okrs/{okrId}/key-results
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param okrId - The parent OKR ID
 * @param data - The Key Result data to create
 * @returns Promise resolving to the created Key Result
 */
export const createKeyResult = async (
  tenantDomain: string,
  okrId: string,
  data: CreateKeyResultData
): Promise<KeyResult> => {
  const path = `/performance/okrs/${okrId}/key-results`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as KeyResult
}

/**
 * List all Key Results for a specific OKR
 * GET /performance/okrs/{okrId}/key-results
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param okrId - The parent OKR ID
 * @param params - Optional query parameters (limit, nextToken)
 * @returns Promise resolving to paginated list of Key Results
 */
export const listKeyResultsByOKR = async (
  tenantDomain: string,
  okrId: string,
  params?: {
    limit?: number
    nextToken?: string
  }
): Promise<KeyResultsListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) {
    queryParams.append('limit', params.limit.toString())
  }

  if (params?.nextToken) {
    queryParams.append('nextToken', params.nextToken)
  }

  const path = `/performance/okrs/${okrId}/key-results${queryParams.toString() ? `?${queryParams.toString()}` : ''}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()

  // Extract pagination metadata from headers
  const nextToken = response.headers?.['x-next-token']
  const itemCount = response.headers?.['x-item-count']

  return {
    keyResults: responseData as unknown as KeyResult[],
    nextToken,
    itemCount: itemCount ? parseInt(itemCount, 10) : (responseData as any[]).length
  }
}

/**
 * Get a single Key Result by ID
 * GET /performance/key-results/{krId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The unique identifier of the Key Result
 * @returns Promise resolving to the Key Result
 */
export const getKeyResult = async (
  tenantDomain: string,
  krId: string
): Promise<KeyResult> => {
  const path = `/performance/key-results/${krId}`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as KeyResult
}

/**
 * Update a Key Result
 * PUT /performance/key-results/{krId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The unique identifier of the Key Result to update
 * @param data - The fields to update
 * @returns Promise resolving to the updated Key Result
 */
export const updateKeyResult = async (
  tenantDomain: string,
  krId: string,
  data: UpdateKeyResultData
): Promise<KeyResult> => {
  const path = `/performance/key-results/${krId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as KeyResult
}

/**
 * Update Key Result progress (currentValue) and auto-recalculate parent OKR
 * PUT /performance/key-results/{krId}/progress
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The unique identifier of the Key Result
 * @param currentValue - The new current value
 * @returns Promise resolving to the updated Key Result
 */
export const updateKeyResultProgress = async (
  tenantDomain: string,
  krId: string,
  currentValue: number
): Promise<KeyResult> => {
  const path = `/performance/key-results/${krId}/progress`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData({ currentValue }),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as KeyResult
}

/**
 * Delete a Key Result (soft delete)
 * DELETE /performance/key-results/{krId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The unique identifier of the Key Result to delete
 * @returns Promise resolving when deletion is complete
 */
export const deleteKeyResult = async (
  tenantDomain: string,
  krId: string
): Promise<void> => {
  const path = `/performance/key-results/${krId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

// ============================================================================
// Feedbacks API Functions
// ============================================================================

/**
 * Get current user's recent feedbacks
 *
 * Returns the 10 most recent feedbacks for the authenticated user.
 * This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @returns Promise resolving to array of recent Feedback objects
 */
export const getMyRecentFeedbacks = async (
  tenantDomain: string
): Promise<Feedback[]> => {
  const path = '/performance/feedbacks/me/recent'

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  // Backend returns { items: Feedback[], pagination: {...} }
  return (data as any)?.items || []
}

/**
 * List current user's feedbacks with pagination
 *
 * Returns paginated list of feedbacks for the authenticated user.
 * This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination and filtering parameters
 * @returns Promise resolving to paginated list of Feedbacks
 */
export const getMyFeedbacks = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'draft' | 'delivered' | 'follow-up-scheduled'
  }
): Promise<FeedbacksListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
  if (params?.status) queryParams.append('status', params.status)

  const path = '/performance/feedbacks/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as Feedback[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

// ============================================================================
// One-on-One Meetings API Functions
// ============================================================================

/**
 * Get current user's 1-on-1 meetings with pagination and filtering
 *
 * Returns paginated list of 1-on-1 meetings for the authenticated user.
 * This endpoint follows the /me self-service pattern.
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination and filtering parameters
 * @returns Promise resolving to paginated list of OneOnOneMeetings
 */
export const getMyOneOnOnes = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: 'scheduled' | 'confirmed' | 'completed' | 'cancelled'
    upcoming?: boolean
  }
): Promise<OneOnOneMeetingsListResponse> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
  if (params?.status) queryParams.append('status', params.status)
  if (params?.upcoming !== undefined) queryParams.append('upcoming', params.upcoming.toString())

  const path = '/performance/one-on-ones/me' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  const items = data as unknown as OneOnOneMeeting[]

  const pagination = extractPaginationMetadata(response.headers, items.length)

  return {
    items,
    pagination
  }
}

// ============================================================================
// Score Calculation Helpers
// ============================================================================

/**
 * Calculate overall score from evaluation scores (ratings)
 *
 * Computes the weighted average score across all evaluation items.
 * Each score's weight is determined by the corresponding EvaluationItem's weight.
 *
 * @param scores - Array of evaluation scores with their weights
 * @returns Overall weighted average score (1-5 scale), or 0 if no scores
 *
 * @example
 * const scores = [
 *   { itemId: 'item-1', score: 4, weight: 5, ... },
 *   { itemId: 'item-2', score: 3, weight: 3, ... }
 * ]
 * const overallScore = calculateOverallScore(scores)
 * // Result: (4*5 + 3*3) / (5+3) = 3.625
 */
export const calculateOverallScore = (
  scores: Array<{ score: number; weight?: number }>
): number => {
  if (!scores || scores.length === 0) {
    return 0
  }

  // Calculate weighted sum and total weight
  let weightedSum = 0
  let totalWeight = 0

  scores.forEach((rating) => {
    const weight = rating.weight || 1 // Default weight is 1 if not specified
    weightedSum += rating.score * weight
    totalWeight += weight
  })

  // Return weighted average, rounded to 2 decimal places
  return totalWeight > 0 ? Math.round((weightedSum / totalWeight) * 100) / 100 : 0
}

/**
 * Determine performance band based on score and parameters
 *
 * Maps a numeric score to a performance band classification using the
 * configured performance parameters (e.g., "Excepcional", "Adequado").
 *
 * @param score - The overall score (1-5 scale)
 * @param parameters - Array of performance parameters defining score bands
 * @returns The name of the matching performance band, or "Não Classificado" if no match
 *
 * @example
 * const parameters = [
 *   { name: "Excepcional", minScore: 4.5, maxScore: 5, ... },
 *   { name: "Adequado", minScore: 3.0, maxScore: 4.4, ... }
 * ]
 * const band = determinePerformanceBand(4.2, parameters)
 * // Result: "Adequado"
 */
export const determinePerformanceBand = (
  score: number,
  parameters: PerformanceParameter[]
): string => {
  if (!parameters || parameters.length === 0) {
    return 'Não Classificado'
  }

  // Filter active parameters and sort by order
  const activeParameters = parameters
    .filter((param) => param.status === 'active')
    .sort((a, b) => a.order - b.order)

  // Find the first parameter where score falls within the range
  const matchingParameter = activeParameters.find(
    (param) => score >= param.minScore && score <= param.maxScore
  )

  return matchingParameter ? matchingParameter.name : 'Não Classificado'
}

// ============================================================================
// Quick Feedback Creation and Updates
// ============================================================================

/**
 * Create quick feedback data structure for backend
 */
export interface CreateQuickFeedbackData {
  category: 'Reconhecimento' | 'Construtivo' | 'Crítico'
  toEmployeeId: string
  toEmployeeName: string
  project?: string
  context: string
  observation: string
  impact: string
  nextSteps: string
  status?: 'draft' | 'delivered' | 'follow-up-scheduled'
}

/**
 * Helper to map frontend form data to backend COIN/SBI structure
 */
export const mapFormDataToQuickFeedback = (formData: any, recipientEmployee: any): CreateQuickFeedbackData => {
  // Map category (capitalize first letter)
  const categoryMap: Record<string, 'Reconhecimento' | 'Construtivo' | 'Crítico'> = {
    'reconhecimento': 'Reconhecimento',
    'construtivo': 'Construtivo',
    'critico': 'Crítico'
  }

  const category = categoryMap[formData.tipo] || 'Construtivo'

  // Build context string from contexto object
  const contextParts: string[] = []
  if (formData.contexto.projeto) contextParts.push(`Projeto: ${formData.contexto.projeto}`)
  if (formData.contexto.data) contextParts.push(`Data: ${formData.contexto.data}`)
  if (formData.contexto.linkWrike) contextParts.push(`Referência: ${formData.contexto.linkWrike}`)
  if (formData.categorias?.length > 0) contextParts.push(`Categorias: ${formData.categorias.join(', ')}`)

  const context = contextParts.length > 0
    ? contextParts.join(' | ')
    : 'Feedback geral'

  // Build impact string from impacto object
  const impactParts: string[] = []
  if (formData.impacto.tipos?.length > 0) {
    impactParts.push(`Áreas impactadas: ${formData.impacto.tipos.join(', ')}`)
  }
  if (formData.impacto.detalhamento) {
    impactParts.push(formData.impacto.detalhamento)
  }

  const impact = impactParts.length > 0
    ? impactParts.join('\n\n')
    : formData.observacao // Fallback to observation if no specific impact

  // Build nextSteps string from expectativa, acordo, and suporte
  const nextStepsParts: string[] = []
  if (formData.expectativaPadrao) {
    nextStepsParts.push(`Expectativa/Padrão: ${formData.expectativaPadrao}`)
  }
  if (formData.acordo.acao) {
    let acordoText = `Ação: ${formData.acordo.acao}`
    if (formData.acordo.responsavel) acordoText += ` | Responsável: ${formData.acordo.responsavel}`
    if (formData.acordo.prazo) acordoText += ` | Prazo: ${formData.acordo.prazo}`
    nextStepsParts.push(acordoText)
  }
  if (formData.suporteNecessario) {
    nextStepsParts.push(`Suporte Necessário: ${formData.suporteNecessario}`)
  }
  if (formData.followUpDate) {
    nextStepsParts.push(`Follow-up agendado: ${formData.followUpDate.toLocaleDateString('pt-BR')}`)
  }

  const nextSteps = nextStepsParts.length > 0
    ? nextStepsParts.join('\n\n')
    : 'A definir'

  return {
    category,
    toEmployeeId: recipientEmployee?.employeeId || 'unknown',
    toEmployeeName: formData.destinatario.nome,
    project: formData.contexto.projeto || undefined,
    context,
    observation: formData.observacao,
    impact,
    nextSteps
  }
}

/**
 * Create a quick feedback (draft or delivered)
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param data - The feedback data to create
 * @returns Promise resolving to the created Feedback object
 */
export const createQuickFeedback = async (
  tenantDomain: string,
  data: CreateQuickFeedbackData
): Promise<Feedback> => {
  const path = '/performance/feedbacks/quick'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(data),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as Feedback
}

/**
 * Update an existing feedback (e.g., change status from draft to delivered)
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param feedbackId - The ID of the feedback to update
 * @param updates - Partial updates to apply
 * @returns Promise resolving to the updated Feedback object
 */
export const updateFeedback = async (
  tenantDomain: string,
  feedbackId: string,
  updates: Partial<CreateQuickFeedbackData>
): Promise<Feedback> => {
  const path = `/performance/feedbacks/${feedbackId}`

  const restOperation = put({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(updates),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const responseData = await response.body.json()
  return responseData as unknown as Feedback
}

// ============================================================================
// Individual Evaluation System API Functions
// ============================================================================

/**
 * Calculate objective score from KR evaluator scores
 * GET /performance/okrs/{okrId}/score
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param okrId - The OKR ID to calculate score for
 * @returns Promise resolving to the objective score calculation result
 */
export const calculateObjectiveScore = async (
  tenantDomain: string,
  okrId: string
): Promise<{
  okrId: string
  score: number | null
  message?: string
  breakdown: {
    scoredKRs: number
    totalKRs: number
    krScores: Array<{
      krId: string
      krName: string
      avgScore: number
      weight: number
      evaluatorCount: number
    }>
  }
}> => {
  const path = `/performance/okrs/${okrId}/score`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as any
}

/**
 * Get evaluation queue for current evaluator
 * GET /performance/evaluator-assignments/my-queue
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional pagination parameters
 * @returns Promise resolving to the evaluation queue with pagination info
 */
export const getMyEvaluationQueue = async (
  tenantDomain: string,
  params?: {
    limit?: number
    offset?: number
  }
): Promise<{
  items: Array<{
    assignment: any
    employee: any
    okrsCount: number
    totalKRs: number
    scoredKRs: number
    progress: number
    status: 'in-progress' | 'completed'
  }>
  pagination: {
    total: number
    limit: number
    offset: number
    hasMore: boolean
  }
}> => {
  const queryParams = new URLSearchParams()

  if (params?.limit) queryParams.append('limit', params.limit.toString())
  if (params?.offset) queryParams.append('offset', params.offset.toString())

  const path = `/performance/evaluator-assignments/my-queue${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as any
}

/**
 * Generate presigned upload URL for evidence file
 * POST /performance/key-results/{krId}/evidence/upload-url
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The Key Result ID
 * @param fileInfo - File information (fileName, fileSize, mimeType)
 * @returns Promise resolving to upload URL and file key
 */
export const generateEvidenceUploadUrl = async (
  tenantDomain: string,
  krId: string,
  fileInfo: {
    fileName: string
    fileSize: number
    mimeType: string
  }
): Promise<{ uploadUrl: string; fileKey: string }> => {
  const path = `/performance/key-results/${krId}/evidence/upload-url`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(fileInfo),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as any
}

/**
 * Create evidence metadata after S3 upload
 * POST /performance/key-results/{krId}/evidence
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The Key Result ID
 * @param evidence - Evidence metadata
 * @returns Promise resolving to the created Evidence
 */
export const createEvidence = async (
  tenantDomain: string,
  krId: string,
  evidence: {
    fileName: string
    fileKey: string
    fileSize: number
    mimeType: string
    description?: string
  }
): Promise<any> => {
  const path = `/performance/key-results/${krId}/evidence`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(evidence),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data
}

/**
 * List evidence files for a KR
 * GET /performance/key-results/{krId}/evidence
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The Key Result ID
 * @returns Promise resolving to list of evidence files
 */
export const listEvidence = async (
  tenantDomain: string,
  krId: string
): Promise<{ evidence: any[] }> => {
  const path = `/performance/key-results/${krId}/evidence`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as any
}

/**
 * Generate presigned download URL for evidence file
 * GET /performance/key-results/{krId}/evidence/{evidenceId}/download-url
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The Key Result ID
 * @param evidenceId - The Evidence ID
 * @returns Promise resolving to the download URL
 */
export const generateEvidenceDownloadUrl = async (
  tenantDomain: string,
  krId: string,
  evidenceId: string
): Promise<string> => {
  const path = `/performance/key-results/${krId}/evidence/${evidenceId}/download-url`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return (data as { downloadUrl: string }).downloadUrl
}

/**
 * Submit or update KR score
 * POST /performance/key-results/{krId}/scores
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The Key Result ID
 * @param scoreData - Score data (score, comments)
 * @returns Promise resolving to the submitted score
 */
export const submitKRScore = async (
  tenantDomain: string,
  krId: string,
  scoreData: {
    score: number
    comments?: string
  }
): Promise<any> => {
  const path = `/performance/key-results/${krId}/scores`

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(scoreData),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data
}

/**
 * List all scores for a KR
 * GET /performance/key-results/{krId}/scores
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param krId - The Key Result ID
 * @returns Promise resolving to scores and average
 */
export const listKRScores = async (
  tenantDomain: string,
  krId: string
): Promise<{ evaluatorScores: any[]; averageScore: number | null }> => {
  const path = `/performance/key-results/${krId}/scores`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as any
}

/**
 * Create evaluator assignment
 * POST /performance/evaluator-assignments
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param assignment - Assignment data (evaluateeId, evaluatorId, period, assignedBy)
 * @returns Promise resolving to the created assignment
 */
export const createEvaluatorAssignment = async (
  tenantDomain: string,
  assignment: {
    evaluateeId: string
    evaluatorId: string
    period: { startDate: string; endDate: string }
    assignedBy: string
  }
): Promise<any> => {
  const path = '/performance/evaluator-assignments'

  const restOperation = post({
    apiName: API_NAME,
    path,
    options: {
      body: serializeData(assignment),
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data
}

/**
 * List evaluator assignments
 * GET /performance/evaluator-assignments
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param params - Optional filtering parameters (evaluateeId, evaluatorId)
 * @returns Promise resolving to list of assignments
 */
export const listEvaluatorAssignments = async (
  tenantDomain: string,
  params?: {
    evaluateeId?: string
    evaluatorId?: string
  }
): Promise<{ items: any[] }> => {
  const queryParams = new URLSearchParams()

  if (params?.evaluateeId) queryParams.append('evaluateeId', params.evaluateeId)
  if (params?.evaluatorId) queryParams.append('evaluatorId', params.evaluatorId)

  const path = `/performance/evaluator-assignments${
    queryParams.toString() ? `?${queryParams.toString()}` : ''
  }`

  const restOperation = get({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  const response = await restOperation.response
  const data = await response.body.json()
  return data as any
}

/**
 * Delete evaluator assignment
 * DELETE /performance/evaluator-assignments/{assignmentId}
 *
 * @param tenantDomain - The tenant domain for multi-tenancy isolation
 * @param assignmentId - The assignment ID to delete
 * @returns Promise resolving when deletion is complete
 */
export const deleteEvaluatorAssignment = async (
  tenantDomain: string,
  assignmentId: string
): Promise<void> => {
  const path = `/performance/evaluator-assignments/${assignmentId}`

  const restOperation = del({
    apiName: API_NAME,
    path,
    options: {
      headers: createImpersonatedApiHeaders()
    }
  })

  await restOperation.response
}

// ============================================================================
// Individual OKR Admin Listing API Functions
// ============================================================================

export const getAllIndividualOKRs = async (
  tenantDomain: string,
  params?: {
    limit?: number
    nextToken?: string
    status?: string
    search?: string
  }
) => {
  try {
    const queryParams = new URLSearchParams()

    if (params?.limit) queryParams.append('limit', params.limit.toString())
    if (params?.nextToken) queryParams.append('nextToken', params.nextToken)
    if (params?.status && params.status !== 'all') queryParams.append('status', params.status)
    if (params?.search) queryParams.append('search', params.search)

    const path = '/performance/okrs/individual-all' + (queryParams.toString() ? `?${queryParams.toString()}` : '')

    const restOperation = get({
      apiName: API_NAME,
      path,
      options: {
        headers: createImpersonatedApiHeaders()
      }
    })

    const response = await restOperation.response
    const data = await response.body.json()
    const items = data as unknown as any[]

    const pagination = extractPaginationMetadata(response.headers, items.length)

    return {
      items,
      pagination
    }
  } catch (error) {
    console.error('Error getting all individual OKRs:', error)
    throw error
  }
}

